import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import i18n from '../../../i18n';

/**
 * Column-config-driven report export (Excel / PDF / CSV).
 *
 * Column shape:
 *   { key, header, isMoney?, isCount?, total?, format?(value) }
 *     - isMoney : number, formatted #,##0.00 / right-aligned
 *     - isCount : number, formatted #,##0
 *     - total   : include a summed value in the totals row (defaults to
 *                 true for isMoney/isCount columns)
 *     - format  : custom display formatter (used for PDF/CSV text)
 *
 * No hardcoded column indices — totals/number-formats are derived from
 * the column flags, so adding/reordering columns can't silently break.
 */

const wantsTotal = (col) => col.total === true || (col.total !== false && (col.isMoney || col.isCount));

/**
 * Back-compat: legacy callers pass bare {key,header} columns and relied on
 * the old hardcoded totals/number-formats for these two keys. Infer the
 * flags so their exports keep working without per-report changes.
 */
const normalizeColumns = (columns) => (columns || []).map((c) => {
    if (c.isMoney || c.isCount) return c;
    if (c.key === 'total_net_sales' || c.key === 'net_sales') return { ...c, isMoney: true };
    if (c.key === 'total_transaction_count' || c.key === 'transaction_count') return { ...c, isCount: true };
    return c;
});

const computeTotals = (columns, data) => {
    const totals = {};
    columns.forEach((col) => {
        if (wantsTotal(col)) {
            totals[col.key] = data.reduce((sum, item) => sum + (Number(item[col.key]) || 0), 0);
        }
    });
    return totals;
};

const cellText = (col, value) => {
    if (col.format) return col.format(value);
    return value;
};

// ── Excel ────────────────────────────────────────────────────────────────
export const exportToExcel = (reportHeading, metadata, rawColumns, data, fileName) => {
    const columns = normalizeColumns(rawColumns);
    const totals = computeTotals(columns, data);

    const exportRows = data.map((item) => {
        const row = {};
        columns.forEach((col) => { row[col.header] = item[col.key]; });
        return row;
    });

    // Totals row
    const totalsRow = {};
    let labelPlaced = false;
    columns.forEach((col) => {
        if (Object.prototype.hasOwnProperty.call(totals, col.key)) {
            totalsRow[col.header] = totals[col.key];
        } else if (!labelPlaced) {
            totalsRow[col.header] = i18n.t('Total');
            labelPlaced = true;
        } else {
            totalsRow[col.header] = '';
        }
    });
    exportRows.push(totalsRow);

    const worksheet = XLSX.utils.aoa_to_sheet([[reportHeading]]);
    worksheet['A1'] = { t: 's', v: reportHeading, s: { font: { sz: 14, bold: true }, alignment: { horizontal: 'center' } } };
    worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: columns.length - 1 } }];

    const metadataArray = (metadata || []).map((item) => [Array.isArray(item) ? item.join(': ') : item]);
    XLSX.utils.sheet_add_aoa(worksheet, metadataArray, { origin: 'A2' });

    const headerRowIdx = (metadata?.length || 0) + 2; // 1-based row for headers
    XLSX.utils.sheet_add_aoa(worksheet, [columns.map((c) => i18n.t(c.header))], { origin: `A${headerRowIdx}` });
    XLSX.utils.sheet_add_json(worksheet, exportRows, { origin: `A${headerRowIdx + 1}`, skipHeader: true });

    // Number formats by column flag
    const startRow = headerRowIdx; // 0-based index of first data row in encode_cell
    columns.forEach((col, c) => {
        if (!col.isMoney && !col.isCount) return;
        const z = col.isMoney ? '#,##0.00' : '#,##0';
        for (let r = 0; r < exportRows.length; r++) {
            const ref = XLSX.utils.encode_cell({ r: startRow + r, c });
            if (worksheet[ref] && typeof worksheet[ref].v === 'number') {
                worksheet[ref].t = 'n';
                worksheet[ref].z = z;
            }
        }
    });

    // Auto widths
    worksheet['!cols'] = columns.map((col) => {
        const maxLen = Math.max(
            col.header.length,
            ...exportRows.map((row) => (row[col.header] != null ? String(row[col.header]).length : 0)),
        );
        return { wch: maxLen + 2 };
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
    XLSX.writeFile(workbook, fileName);
};

// ── PDF ──────────────────────────────────────────────────────────────────
export const exportToPDF = (reportHeading, metadata, rawColumns, data, fileName, landscape = false) => {
    const columns = normalizeColumns(rawColumns);
    const doc = new jsPDF({ orientation: landscape ? 'landscape' : 'portrait' });
    const totals = computeTotals(columns, data);

    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text(reportHeading, doc.internal.pageSize.getWidth() / 2, 10, { align: 'center' });

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const lineHeight = 5;
    let startY = 20;
    (metadata || []).forEach((item, i) => {
        doc.text(Array.isArray(item) ? item.join(': ') : item, 10, startY + i * lineHeight);
    });
    startY += (metadata?.length || 0) * lineHeight + 5;

    const head = [columns.map((c) => i18n.t(c.header))];
    const body = data.map((item) => columns.map((col) => cellText(col, item[col.key])));

    let labelPlaced = false;
    const footRow = columns.map((col) => {
        if (Object.prototype.hasOwnProperty.call(totals, col.key)) {
            return col.isMoney
                ? Number(totals[col.key]).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : Number(totals[col.key]).toLocaleString();
        }
        if (!labelPlaced) { labelPlaced = true; return i18n.t('Total'); }
        return '';
    });

    // Right-align money columns
    const columnStyles = {};
    columns.forEach((col, i) => { if (col.isMoney || col.isCount) columnStyles[i] = { halign: 'right' }; });

    doc.autoTable({
        startY,
        head,
        body,
        foot: [footRow],
        styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
        headStyles: { fontSize: 7, fillColor: [37, 99, 235], textColor: [255, 255, 255] },
        footStyles: { fontSize: 7, fillColor: [37, 99, 235], textColor: [255, 255, 255] },
        columnStyles,
        margin: { top: 10, right: 10, bottom: 10, left: 10 },
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.text(i18n.t('Page {{page}} of {{pages}}', { page: i, pages: pageCount }), doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' });
    }

    doc.save(fileName);
};

// ── CSV ──────────────────────────────────────────────────────────────────
const csvEscape = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const exportToCSV = (reportHeading, metadata, rawColumns, data, fileName) => {
    const columns = normalizeColumns(rawColumns);
    const totals = computeTotals(columns, data);
    const lines = [];
    lines.push(csvEscape(reportHeading));
    (metadata || []).forEach((item) => lines.push(csvEscape(Array.isArray(item) ? item.join(': ') : item)));
    lines.push('');
    lines.push(columns.map((c) => csvEscape(i18n.t(c.header))).join(','));
    data.forEach((item) => {
        lines.push(columns.map((col) => csvEscape(cellText(col, item[col.key]))).join(','));
    });
    let labelPlaced = false;
    lines.push(columns.map((col) => {
        if (Object.prototype.hasOwnProperty.call(totals, col.key)) return csvEscape(totals[col.key]);
        if (!labelPlaced) { labelPlaced = true; return csvEscape(i18n.t('Total')); }
        return '';
    }).join(','));

    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};
